import { type OasisTaskViewModel } from '@generated';
import type { FormikValues } from 'formik';
import type { FormControlModelExtended } from '~/libraries/forms/types';

export function calculateCollaborationDivergence(oasisTask: OasisTaskViewModel, originalValues: FormikValues, currentValues: FormikValues): boolean {
	const fieldNames = oasisTask.oasisDefinition.documentationFormFields.map(x => x.name);
	const collaborationValues = fieldNames.reduce((acc, fieldName) => {
		const fieldValue = currentValues[fieldName];
		if (fieldValue !== undefined) {
			acc[fieldName] = currentValues[fieldName];
		}
		return acc;
	}, {} as FormikValues);

	const originalCollaborationValues = fieldNames.reduce((acc, fieldName) => {
		if (collaborationValues[fieldName] !== undefined) {
			acc[fieldName] = originalValues[fieldName];
		}
		return acc;
	}, {} as FormikValues);

	return JSON.stringify(collaborationValues) !== JSON.stringify(originalCollaborationValues);
}

//marks the option values that were originally selected in the collaboration, so that they can be displayed differently in the UI
export function markOriginalCollaborationAnswers(
	formControls: FormControlModelExtended[],
	originalCollaborationValues: FormikValues,
	markAsUpdated: boolean = false
): FormControlModelExtended[] {
	return formControls.map((control: FormControlModelExtended) => {
		if (control.name && originalCollaborationValues[control.name] !== undefined && originalCollaborationValues[control.name] !== null) {
			const collaborationValue = originalCollaborationValues[control.name];

			if (control.options && control.options.length > 0) {
				const collaborationValueArray = collaborationValue ? collaborationValue.split(',') : [];
				control.options = control.options.map(option => {
					if (collaborationValueArray.includes(option.value)) {
						if (!markAsUpdated) {
							return { ...option, isCollaborationOriginal: true };
						}
						//only mark as updated if the value is different than the original value
						return option.isCollaborationOriginal
							? { ...option, isCollaborationOriginal: false }
							: { ...option, isCollaborationUpdated: true };
					}
					return option;
				});
			}
		}

		if (control.formControls) {
			return { ...control, formControls: markOriginalCollaborationAnswers(control.formControls, originalCollaborationValues, markAsUpdated) };
		}

		if (control.options && control.options.length > 0) {
			control.options = control.options.map(option => {
				if (option.formControls) {
					return {
						...option,
						formControls: markOriginalCollaborationAnswers(option.formControls, originalCollaborationValues, markAsUpdated)
					};
				}
				return option;
			});
		}

		return control;
	});
}

export function markOriginalAndUpdatedCollaborationAnswers(
	formControls: FormControlModelExtended[],
	originalCollaborationValues: FormikValues,
	updatedCollaborationValues: FormikValues
): FormControlModelExtended[] {
	const controlsWithOriginalAnswers = markOriginalCollaborationAnswers(formControls, originalCollaborationValues);
	return markOriginalCollaborationAnswers(controlsWithOriginalAnswers, updatedCollaborationValues, true);
}

interface CollaborationDivergence {
	fieldName: string;
	originalValue: unknown;
	currentValue: unknown;
}

export function getCollaborationDivergences(
	oasisTask: OasisTaskViewModel,
	originalValues: FormikValues,
	currentValues: FormikValues
): CollaborationDivergence[] {
	const fieldNames = oasisTask.oasisDefinition.documentationFormFields.map(x => x.name);
	const divergences: CollaborationDivergence[] = [];

	fieldNames.forEach(fieldName => {
		const originalValue = originalValues[fieldName];
		const currentValue = currentValues[fieldName];
		if (originalValue !== currentValue) {
			divergences.push({
				fieldName,
				originalValue,
				currentValue
			});
		}
	});
	return divergences;
}
